import React, { useMemo, useState } from 'react';
import { Box, Typography, Avatar, Button, Tooltip, Alert, CircularProgress, Grid } from '@mui/material';
import { MdPayment, } from 'react-icons/md';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { openModal } from '../../redux/slice/AppSlice';
import ReusableTable from '../../components/common/Table';
import { useUsers } from '../../hooks/UseUsers';
import { formatPrice } from '../../utils/mappingData';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import ProcessPayoutModal from './compoent/ProcessPayoutModal';
import SearchField from '../../components/common/SearchFields';

// Define columns
const columns = [
    { id: 'user', label: 'USER DETAILS', width: '20%' , sortable: true  },
    { id: 'paymentMethod', label: 'PAYMENT METHOD', width: '18%' , sortable: false  },
    { id: 'AvailableBalance', label: 'AVAILABLE BALANCE', width: '25%' , sortable: true  },
    { id: 'reservedBalance', label: 'RESERVED BALANCE', width: '25%' , sortable: true  },
    { id: 'totalEarnings', label: 'TOTAL EARNINGS', width: '25%' , sortable: true  },
    { id: 'actions', label: 'ACTIONS', width: '18%' , sortable: false  },
];

function ArtistBalance() {
    
    // Fetch users from Firebase with real-time updates
    const { users, loading, error, refetch } = useUsers({
        realtime: false,  // Enable real-time updates
        activeOnly: true, // Get all users including deleted
        type: 'artist',   // Only fetch artists
    });
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [confirmState, setConfirmState] = useState({
        open: false,
        artist: null,
    });
    const [searchQuery, setSearchQuery] = useState('');
    
    const userData = useMemo(() => {
        let data = users.filter(user =>
            user.type !== 'Admin' && user.type !== 'superAdmin' && !user.is_deleted
        );

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            data = data.filter((user) => {
                const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
                const email = (user.email || '').toLowerCase();
                return fullName.includes(q) || email.includes(q);
            });
        }

        return data;
    }, [users, searchQuery]);

    // Get initials — defined before tableData's useMemo because it's invoked
    // during JSX construction inside that memo's factory.
    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0).toUpperCase() || '';
        const last = lastName?.charAt(0).toUpperCase() || '';
        return first + last || 'A';
    };

    // ✅ Validate payout method is complete
    const isPayoutMethodComplete = (payoutMethod) => {
        if (!payoutMethod || typeof payoutMethod !== 'object') {
            return false;
        }

        // Check if all required fields are present
        const { type } = payoutMethod;
        return Boolean(type);
    };

    // Open payment modal
    const handlePayout = (artist) => {
        const balance = parseFloat(artist?.available_balance || 0);
        // ✅ Check if balance is 0 or less
        if (!isPayoutMethodComplete(artist?.payout_method)) {
            setConfirmState({
                open: true,
                artist: artist,
                title: "Incomplete Account Setup",
                message: `${artist.first_name || 'This artist'} has not completed their payout account setup. Please ask them to configure their payment method in the mobile app.`,
            });
        }else if(balance <= 0){
            setConfirmState({
                open: true,
                artist: artist,
                title: "No Balance Available",
                message: `${artist.first_name || 'This artist'} has a balance of $0.00. No payout can be processed.`,
            });
        }
        else {
            // ✅ Open payment modal directly
            dispatch(openModal({
                Children: <ProcessPayoutModal user={artist} onSuccess={refetch}/>,
                isCenter: true,
                width: 440,
                isCloseIcon: true,
            }));
        }
    };

    // Close Dialog
    const handleConfirmClose = () => {
        setConfirmState({ open: false, artist: null });
    };


    // ✅ Memoize so JSX rows are only rebuilt when the underlying data changes.
    // ⚠️ Must sit ABOVE the loading/error early returns — Rules of Hooks
    // require every hook call to run in the same order on every render.
    const tableData = useMemo(() => {
        if (!Array.isArray(userData)) return [];
        return userData.map((user) => ({
        _userId: user.id,
        user: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                    src={user.profile_picture || ''}
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.blackColor.black1,
                        width: 40,
                        height: 40,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                    }}
                >
                    {!user.profile_picture && getInitials(user.first_name, user.last_name)}
                </Avatar>
                <Box>
                    <Typography
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                        }}
                    >
                        {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No Name'}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {user.email || 'No Email'}
                    </Typography>
                </Box>
            </Box>
        ),
        paymentMethod:(
            <Typography
                sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                }}
            >
                {user?.payout_method?.type || '-'}
            </Typography>
        ),
        balance:(
            <Typography
                sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                }}
            >
                {formatPrice(user?.balance) || '$0.00'}
            </Typography>
        ),
        AvailableBalance:(
            <Typography
                sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                }}
            >
                {formatPrice(user?.available_balance) || '$0.00'}
            </Typography>
        ),
        reservedBalance:(
            <Typography
                sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                }}
            >
                {formatPrice(user?.reserved_balance) || '$0.00'}
            </Typography>
        ),
        _sort_AvailableBalance: parseFloat(user?.available_balance || 0),
        _sort_reservedBalance: parseFloat(user?.reserved_balance || 0),
        totalEarnings: (
            <Typography
                sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                }}
            >
                {formatPrice(user?.total_earnings) || '$0.00'}
            </Typography>
        ),
        _sort_totalEarnings: parseFloat(user?.total_earnings || 0),
        actions: (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="Send Payout" arrow>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<MdPayment size={15} />}
                        onClick={(e) => { e.stopPropagation(); handlePayout(user); }}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.blackColor.black1,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            px: 2,
                            py: 0.6,
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        Payout
                    </Button>
                </Tooltip>
            </Box>
        ),
    }));
    }, [userData, theme]);

    // ── Early returns AFTER all hooks (Rules of Hooks) ──
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                }}
            >
                <CircularProgress sx={{ color: theme?.palette?.primary?.main }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ width: "100%", p: 3 }}>
                <Alert severity="error">
                    Error loading data: {error}
                </Alert>
            </Box>
        );
    }


    return (
        <Box sx={{display: "flex" , flexDirection: "column" , gap: 2}}>

            {/* Header */}
            <Box>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                >
                    Artist Balance
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                    Manage artist payouts and balances
                </Typography>
            </Box>

            {/* Search Field */}
            <Grid container spacing={2}>
                <Grid size={{ lg: 6 }}>
                    <SearchField
                        searchQuery={searchQuery}
                        handleSearchChange={(value) => {
                            if (typeof value === 'string') setSearchQuery(value);
                            else if (value?.target?.value !== undefined) setSearchQuery(value.target.value);
                        }}
                        placeholder="Search artists by name or email..."
                    />
                </Grid>
            </Grid>

            {/* Table */}
            <ReusableTable
                columns={columns}
                data={tableData}
                pagination={true}
                defaultRowsPerPage={25}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowClick={(row) => navigate(`/artist-balance/${row._userId}`)}
            />

            {/* Confirmation Modal for zero balance */}
            <ConfirmationModal
                open={confirmState.open}
                onClose={handleConfirmClose}
                onCancel={handleConfirmClose}
                onConfirm={()=>{}}
                variant="warning"
                title={confirmState?.title || "No Balance Available"}
                message={confirmState?.message || `${confirmState.artist?.first_name || 'This artist'} has a balance of $0.00. Do you still want to proceed?`}
                confirmText={null}
                cancelText="Cancel"
            />

        </Box>
    )
}

export default ArtistBalance